import type {} from '@mui/x-date-pickers/AdapterDayjs';
import {
    DatePicker,
    DatePickerProps,
    DateValidationError,
    PickerChangeHandlerContext,
    PickersActionBarAction,
} from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { InputAdornment, TextFieldProps, Tooltip } from '@mui/material';
import { ReactElement, ReactNode, useState } from 'react';
import { WarningAmberOutlined } from '@mui/icons-material';

import { PoolDatePickerActionBar } from './pool-date-picker-action-bar';

export interface PoolDatePickerProps extends DatePickerProps<Dayjs> {
    name?: string;
    required?: boolean;
    helperText?: ReactNode;
    error?: boolean;
    fullWidth?: boolean;
    /**
     * Keeps past days greyed out in the calendar but says nothing under the
     * field when one is set — for a caller that says it itself, shorter (#2251).
     */
    quietPast?: boolean;
}

export const PoolDatePicker = (props: PoolDatePickerProps): ReactElement => {
    const {
        name,
        required,
        slotProps,
        helperText,
        error,
        disabled,
        readOnly,
        fullWidth = true,
        minDate,
        quietPast,
        ...datePickerProps
    } = props;
    const [pickerError, setPickerError] = useState<DateValidationError>(null);

    const handleChange = (value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>): void => {
        setPickerError(context.validationError);
        props.onChange?.(value, context);
    };

    const getHelperText = (): ReactNode => {
        if (helperText) {
            return helperText;
        }
        // A day gone by is a note, not an error: under the field it made the
        // row of a document head twice as high and pushed the field out of
        // line (#2262). It is a mark in the field now, said on hover.
        if (pickerError === 'disablePast') {
            return undefined;
        }
        if (pickerError && pickerError !== 'minDate' && pickerError !== 'maxDate' && !disabled && !readOnly) {
            return <FormattedMessage id={'components.datePicker.' + pickerError} />;
        }
        return undefined;
    };

    const pastMark =
        !quietPast && pickerError === 'disablePast' && !helperText && !disabled && !readOnly ? (
            <InputAdornment position="start" sx={{ mr: '2px' }}>
                <Tooltip title={<FormattedMessage id="components.datePicker.disablePast" />}>
                    <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
                </Tooltip>
            </InputAdornment>
        ) : undefined;

    return (
        <DatePicker
            {...datePickerProps}
            slots={{
                actionBar: PoolDatePickerActionBar,
            }}
            slotProps={{
                ...slotProps,
                // The one symbol of a date that Tab stops at in a PALO form grid.
                openPickerButton: { ...slotProps?.openPickerButton, 'data-palo-tab-stop': true } as never,
                textField: {
                    ...slotProps?.textField,
                    name,
                    required,
                    helperText: getHelperText(),
                    fullWidth,
                    error: error && !disabled && !readOnly,
                    disabled: disabled || readOnly,
                    InputProps: {
                        ...(slotProps?.textField as TextFieldProps | undefined)?.InputProps,
                        startAdornment: pastMark,
                    },
                },
                popper: {
                    ...slotProps?.popper,
                    placement: 'bottom-end',
                },
                actionBar: ({ wrapperVariant }) => ({
                    actions: [
                        ...(wrapperVariant === 'desktop' ? [] : ['accept' as PickersActionBarAction]),
                        ...(required ? [] : ['clear' as PickersActionBarAction]),
                    ],
                }),
            }}
            onError={setPickerError}
            onChange={handleChange}
            disabled={disabled}
            minDate={minDate}
        />
    );
};
